<template>
  <MotionBackground />
  <FriedModal
    id="create-entity-modal"
    class="create-entity-modal signup"
    :closable="false"
    size="large"
    :show-backdrop="false"
    :modal="false"
  >
    <template #header>
      <div>
        <FriedGetAcceptLogo class="logo" />
        <div class="signup-header">
          <FriedH2> {{ `👋 ${greeting}` }}</FriedH2>
          <FriedParagraph class="description">
            {{ $t('verify_user_description') }}
          </FriedParagraph>
        </div>
      </div>
    </template>
    <div>
      <FriedMessage v-if="error" class="error" :title="error.title" type="error">
        <i18n-t :keypath="error.description">
          <template #contact_support>
            <FriedLink :normal="true" href="http://help.getaccept.com/" target="_blank">
              {{ $t('contact-support') }}
            </FriedLink>
          </template>
        </i18n-t>
      </FriedMessage>
      <form
        class="verify-user-container"
        :class="{ mobile: isSmallScreenAndDown }"
        @submit.prevent="handleSubmit"
      >
        <FriedInput
          v-model.trim="profileInformation.firstName"
          :autofocus="!profileInformation.firstName"
          data-external="signup-verify-user-first-name-input"
          :label="$t('first-name')"
          autocomplete="first-name"
          :error-message="validation.firstName"
          @update:model-value="handleUpdate({ field: 'firstName', value: $event })"
        />
        <FriedInput
          v-model.trim="profileInformation.lastName"
          :autofocus="!!profileInformation.firstName && !profileInformation.lastName"
          data-external="signup-verify-user-last-name-input"
          :label="$t('last-name')"
          autocomplete="family-name"
          :error-message="validation.lastName"
          @update:model-value="handleUpdate({ field: 'lastName', value: $event })"
        />
        <PhoneNumberInput
          v-model="profileInformation.phoneNumber"
          data-external="signup-verify-user-phone-number-input-number-input"
          :label="isInvitation ? t('phone-number-optional') : t('phone-number')"
          :error-message="validation.phoneNumber"
          @error="validation.phoneNumber = $event"
        />
        <HiddenInput autocomplete="email" />
        <FriedSelect
          :options="languages"
          data-external="signup-verify-user-language-dropdown"
          :model-value="languageObject"
          option-label="label"
          :label="$t('language')"
          :error-message="validation.language"
          @update:model-value="profileInformation = { ...profileInformation, language: $event.key }"
        />
        <FriedInput
          v-model="profileInformation.password"
          :autofocus="!!profileInformation.firstName && !!profileInformation.lastName"
          data-external="signup-verify-user-password-input"
          :type="viewPassword ? 'text' : 'password'"
          :label="$t('password')"
          autocomplete="new-password"
          :error-message="validation.password"
          :help-message="validation.password ? '' : $t('password_help_message')"
          @update:model-value="handleUpdate({ field: 'password', value: $event })"
        >
          <template #append>
            <FriedIconButton
              type="button"
              tabindex="-1"
              class="view-password-button"
              data-external="signup-verify-user-view-password-button"
              :title="$t('show-and-hide-password')"
              @click.prevent="viewPassword = !viewPassword"
            >
              <FriedIcon v-if="!viewPassword" key="1" aria-hidden icon="view" />
              <FriedIcon v-else key="2" aria-hidden icon="view-hide" />
            </FriedIconButton>
          </template>
        </FriedInput>
      </form>
    </div>
    <template #footer>
      <FriedModalButtonsFooter>
        <FriedButton
          data-external="signup-verify-user-cancel-button"
          button-type="secondary"
          :disabled="isLoading"
          class="submit-button"
          @click="handleCancel"
        >
          {{ $t('cancel') }}
        </FriedButton>
        <FriedButton
          type="submit"
          data-external="signup-verify-user-submit-button"
          :loading="isLoading"
          :disabled="disableSubmit"
          class="submit-button"
          @click="handleSubmit"
        >
          {{ $t('next') }}
        </FriedButton>
      </FriedModalButtonsFooter>
    </template>
  </FriedModal>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { getLanguages } from '@getaccept/lib-shared/src/languages/constants/language';
import HiddenInput from '@getaccept/lib-shared/src/hidden-input/HiddenInput.vue';
import PhoneNumberInput from '@getaccept/lib-shared/src/components/PhoneNumberInput.vue';
import { useBreakpoints } from '@getaccept/fried-tofu';
import MotionBackground from '@getaccept/lib-shared/src/components/MotionBackground.vue';
import { useEnvironment } from '@getaccept/lib-shared/src/composables/environment.composable';
import type { ProfileInformation } from '../types/profile-information';
import * as VerifyUserHelper from '../helpers/verify-user.helper';
import { useVerifyUser } from '../composables/verify-user.composable';
import { SignupErrorKey } from '../../common/enums/signup-error-key';
import { useLanguageStore } from '../../language/store/language.store';
import { PasswordValidation } from '../helpers/password-validation';

const props = withDefaults(
  defineProps<{
    signupId: string;
    signupKey: string;
    firstName: string;
    lastName: string;
    isInvitation: boolean;
    version: number;
  }>(),
  {
    firstName: '',
    lastName: '',
    isInvitation: false,
    version: 1,
  }
);
const { isLocalHost } = useEnvironment();
const { t } = useI18n();
const { language } = storeToRefs(useLanguageStore());
const { isSmallScreenAndDown } = useBreakpoints();

const viewPassword = ref(false);

const profileInformation = ref<ProfileInformation>({
  firstName: props.firstName,
  lastName: props.lastName,
  language: language.value,
  phoneNumber: '',
  password: '',
});

const validation = ref<ProfileInformation>({
  firstName: '',
  lastName: '',
  language: '',
  phoneNumber: '',
  password: '',
});
const languages = computed(() => getLanguages());
const languageObject = computed(() =>
  languages.value.find(language => language.key === profileInformation.value.language)
);

const formHasError = computed(() =>
  Object.entries(validation.value)
    .map(entry => entry[1])
    .some(value => !!value)
);

const hasValidPassword = computed(() => {
  const passwordError = PasswordValidation.validateNewPassword(profileInformation.value.password);
  return !passwordError;
});

const disableSubmit = computed(
  () =>
    formHasError.value ||
    !profileInformation.value.firstName ||
    !profileInformation.value.lastName ||
    !hasValidPassword.value ||
    (!props.isInvitation && !profileInformation.value.phoneNumber)
);

const validateForm = (): ProfileInformation => {
  const { password, firstName, lastName } = profileInformation.value;
  const passwordError = PasswordValidation.validateNewPassword(password);

  return {
    password: passwordError,
    firstName: firstName ? '' : t('first-name-is-required'),
    lastName: lastName ? '' : t('last-name-is-required'),
    phoneNumber: validation.value.phoneNumber,
    language: '',
  };
};

const handleUpdate = ({ field, value }: { field: string; value: string }) => {
  profileInformation.value[field] = value;
  if (value) {
    validation.value[field] = '';
  }
};

const { execute: verifyUser, error, isLoading } = useVerifyUser();

const handleSubmit = async () => {
  validation.value = validateForm();
  if (formHasError.value) return;

  const responseUrl = await verifyUser(
    0,
    profileInformation.value,
    props.signupId,
    props.signupKey,
    props.isInvitation,
    props.version
  );
  if (!responseUrl) {
    error.value = VerifyUserHelper.getErrorMessage({ name: '', message: SignupErrorKey.InvalidId });
    return;
  }

  window.location.href = responseUrl;
};

const greeting = computed(() => {
  if (props.firstName) {
    return t('signup_modal_title_with_name', { name: props.firstName });
  }
  return t('signup_modal_title');
});

const handleCancel = () => {
  if (!isLocalHost.value) {
    window.location.href = '/login';
  } else {
    window.location.href = '/';
  }
};

onMounted(() => {
  VerifyUserHelper.trackPageLoaded(props.signupId, props.isInvitation);
});
</script>

<style scoped>
@import '../../common/styles/signup-modal.css';

.error {
  margin-bottom: var(--spacing-100);
}
</style>
